import React from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  Paper,
  TextField,
  Typography,
} from '@material-ui/core';
import { createStyles, makeStyles } from '@material-ui/styles';
import { Close, Edit, Email } from '@material-ui/icons';
import { Skeleton } from '@material-ui/lab';

import { useCampfireLazyQuery } from '../../../../../../global/network/useCampfireLazyQuery';
import { GET_COMMUNICATION_TEMPLATE_DETAILS } from './communication-template-details-model.gql';
import { TemplateItemBody } from './TemplateItemBody';
import { TemplateItemDescription } from './TemplateItemDescription';
import { TemplateItemHeader } from './TemplateItemHeader';
import {
  GetCommunicationTemplateDetails,
  GetCommunicationTemplateDetailsVariables,
  GetCommunicationTemplateDetails_vm_communicationTemplate as CommunicationTemplateType,
} from './__generated__/GetCommunicationTemplateDetails';
import { AlertCard } from '../../../../../../common/cards/alert-card/AlertCard';
import { ConfirmationDialog } from '../../../../../../common/dialogs/ConfirmationDialog';
import { useEndpointFetch } from '../../../../../../global/network/useEndpointFetch';
import { useSnackbar } from '../../../../../../global/config/useSnackbar';
import { useUser } from '../../../../../../global/auth/useUser';
import { useOrgInfo } from '../../../../../../global/auth/use-org-info/use-org-info';
import {
  EnterpriseLockNotice,
  EnterpriseLockTooltip,
  useEnterpriseLock,
} from '../../../../../../global/auth/use-enterprise-lock/use-enterprise-lock';

const templateLockTooltip: EnterpriseLockTooltip = (parentName) =>
  `View only. Email content is set by ${parentName}. You can still turn this email on or off.`;

const useStyles = makeStyles((theme: any) =>
  createStyles({
    button: {
      textTransform: 'none',
    },
    badge: {
      display: 'flex',
      backgroundColor: '#D9D9D9',
      borderRadius: '3px',
      alignItems: 'center',
      padding: '6px',
      '& .icon': {
        fontSize: '15px',
        color: '#0000008A',
      },
      '& .text': {
        fontWeight: 600,
        fontSize: '10px',
        color: theme.color?.grey?.neutral400 ?? '#9E9E9E',
        paddingLeft: '3px',
      },
    },
  })
);

interface TenantItemContentProps {
  communicationTypeId?: string;
  greeting?: string;
  signoff?: string;
  tenantName?: string;
  isLoading: boolean;
  logo?: string | null;
}

export function TemplateItemContent({
  communicationTypeId,
  greeting = 'Hi',
  signoff = 'Thanks,',
  tenantName,
  isLoading,
  logo,
}: TenantItemContentProps) {
  const [communicationType, setCommunicationType] = React.useState<CommunicationTemplateType | null>();
  const [sendTestOpen, setSendTestOpen] = React.useState(false);
  const [expandedView, setExpandedView] = React.useState(false);
  const [isCustomizing, setIsCustomizing] = React.useState(false);
  const [resetConfirmOpen, setResetConfirmOpen] = React.useState(false);
  const sendButtonRef = React.useRef<any>();
  const { user } = useUser();
  const { setSnackbar } = useSnackbar();
  const orgInfo = useOrgInfo();
  const isInTrial = orgInfo?.inTrial || false;

  const [queryCommunicationType, { loading: templateDetailsLoading }] = useCampfireLazyQuery<
    GetCommunicationTemplateDetails,
    GetCommunicationTemplateDetailsVariables
  >(GET_COMMUNICATION_TEMPLATE_DETAILS);

  React.useEffect(() => {
    if (communicationTypeId) {
      queryCommunicationType({
        variables: {
          communicationTypeId,
        },
      }).then((data) => setCommunicationType(data.data?.vm.communicationTemplate));
    }
  }, [communicationTypeId]);
  const [email, setEmail] = React.useState('');
  const updateCommunicationTypeStatus = useEndpointFetch<{ communicationTypeId: string; enabled: boolean }>(
    '/vm/comms/communication-type/save'
  );

  const saveCommunicationTypeContentBlock = useEndpointFetch<{
    communicationTypeId: string;
    communicationTypeCustomContentBlockId?: string;
    block: string;
    content: string | null;
  }>('/vm/comms/communication-content/save');

  const sendSampleEmail = useEndpointFetch<{ email: string; name: string; content: string }>('/vm/comms/send-test');

  const onSaveContentBlock = (block: string, content: string, communicationTypeCustomContentBlockId?: string) => {
    if (!communicationType) {
      return;
    }
    saveCommunicationTypeContentBlock
      .run({
        communicationTypeId: communicationType?.communicationTypeId,
        communicationTypeCustomContentBlockId,
        block,
        content,
      })
      .then((res) => {
        if (!res.ok) {
          setSnackbar({ open: true, message: 'Unable to save template content', variant: 'error' });
          return undefined;
        }
        setSnackbar({ open: true, message: 'Template content saved', variant: 'success' });
        return queryCommunicationType({
          variables: {
            communicationTypeId,
          },
        });
      })
      .then((data) => {
        if (data) setCommunicationType(data.data?.vm.communicationTemplate);
      });
  };

  const onDeleteContentBlock = (communicationTypeCustomContentBlockId: string) => {
    if (!communicationType) {
      return;
    }
    saveCommunicationTypeContentBlock
      .run({
        communicationTypeId: communicationType.communicationTypeId,
        communicationTypeCustomContentBlockId,
        block: '{{FULL_BODY}}',
        content: null,
      })
      .then((res) => {
        if (!res.ok) {
          setSnackbar({ open: true, message: 'Unable to reset template', variant: 'error' });
          return undefined;
        }
        setSnackbar({ open: true, message: 'Template reset to default', variant: 'success' });
        return queryCommunicationType({
          variables: {
            communicationTypeId,
          },
        });
      })
      .then((data) => {
        if (data) setCommunicationType(data.data?.vm.communicationTemplate);
      });
  };

  const fullBodyBlock = communicationType?.contentBlocks?.find((cb) => cb.block === '{{FULL_BODY}}');
  const isCustom = communicationType?.hasCustomTemplate && fullBodyBlock;

  const onResetCustomTemplate = () => {
    setResetConfirmOpen(true);
  };

  const handleConfirmReset = () => {
    if (fullBodyBlock) {
      onDeleteContentBlock(fullBodyBlock.communicationTypeCustomContentBlockId);
    }
    setIsCustomizing(false);
    setResetConfirmOpen(false);
  };

  const onClose = () => setSendTestOpen(false);
  const classes = useStyles();
  const locked = useEnterpriseLock();
  const onUpdateStatus = () => {
    if (!communicationType || !communicationType.isOptional) {
      return;
    }

    const enabled = !communicationType?.status;

    updateCommunicationTypeStatus
      .run({
        communicationTypeId: communicationType?.communicationTypeId,
        enabled,
      })
      .then((res) => {
        if (res.ok) {
          setCommunicationType({
            ...communicationType,
            status: enabled,
          });
        }
      })
      .then(() => {
        return queryCommunicationType({
          variables: {
            communicationTypeId,
          },
        });
      })
      .then((data) => setCommunicationType(data.data?.vm.communicationTemplate));
  };

  const content = communicationType?.content?.content || '';
  const parsedContent = (parsingContent: string) =>
    parsingContent
      .replaceAll('{{FNAME}}', `<strong>${user?.preferredName || 'Volaby User'}</strong>`)
      .replaceAll('{{ORG_NAME}}', `<strong>${tenantName}</strong>`)
      .replaceAll('{tenantName}', `<strong>${tenantName}</strong>`)
      .replaceAll('{{ACTIVITY_NAME}}', '<strong>Activity Name</strong>')
      .replaceAll('{{PROGRAM_NAME}}', '<strong>Program Name</strong>')
      .replaceAll('{{END_DATE}}', '<strong>3rd January, 2023</strong>')
      .replaceAll(
        '{{ROSTER_DATES}}',
        '<strong><li>3rd January, 2023</li><li>5th January, 2023</li><li>7th January, 2023</li><li>8th January, 2023</li><li>10th January, 2023</li></strong>'
      )
      .replaceAll('{{DYNAMIC_CONTENT}}', '<strong>Some more details</strong>')
      .replaceAll('{{ROSTER_DATE}}', '<strong>Roster Date</strong>')
      .replaceAll('{activityDate}', '<strong>Roster Date</strong>')
      .replaceAll('{{ACTIVITY_LOCATION_ADDRESS}}', '<strong>Activity Location Address</strong>')
      .replaceAll('{{ACTIVITY_REMOTE_LOCATION}}', '<strong>Activity Remote Location</strong>')
      .replaceAll('{{SESSION_DETAILS}}', '<strong>Session details</strong>')
      .replaceAll('{{CONTENT}}', '<strong>Additional information</strong>')
      .replaceAll('{{REASON}}', '<strong>Reason information/strong>')
      .replaceAll('{{URL}}', '')
      .replaceAll('{url}', '')
      .replaceAll('{{TASK_NAME}}', '<strong>Task Name</strong>')
      .replaceAll('{{CANCELLATION_REASON}}', '<strong>Cancellation Reason</strong>')
      .replaceAll('{{MESSAGE}}', '<strong>Message from admin/manager here</strong>')
      .replaceAll('{{TASKS}}', '<strong>Incomplete tasks window</strong>')
      .replaceAll('{{ROSTER_ORDINAL_DATE}}', '<strong>Roster Date</strong>')
      .replaceAll('{{INCIDENT_INFO_TEXT}}', '<strong>Incident Information</strong>')
      .replaceAll('{{PROGRAM}}', '<strong>Program</strong>')
      .replaceAll('{{APPLICATION_URL}}', '<strong>Application URL</strong>')
      .replaceAll('{{MANAGER_NAME}}', '<strong>Manager Name</strong>')
      .replaceAll('{{ACTIVITY_RRULE_READABLE}}', '<strong>Schedule</strong>');

  const onSend = () => {
    sendSampleEmail
      .run({
        email: email,
        name: user?.preferredName || 'Volaby User',
        content: parsedContent(communicationType?.stringContentParsed || '')
          .replaceAll('{{INSTRUCTION_BLOCK_HEADER}}', '<strong>Custom type block header</strong>')
          .replaceAll('{{CUSTOM_CONTENT}}', '<strong>Custom type block content</strong>'),
      })
      .then((res) => {
        if (!res.ok) {
          setSnackbar({ open: true, message: 'Unable to send test email', variant: 'error' });
          return;
        }
        setSnackbar({ open: true, message: 'Test email sent', variant: 'success' });
      })
      .finally(() => {
        onClose();
      });
  };

  return (
    <Paper
      style={{
        padding: '30px 35px',
        height: '100%',
        margin: '16px',
        flex: '1 1 0',
        minHeight: 0,
        overflowY: 'scroll',
      }}
      elevation={0}
    >
      {isLoading || templateDetailsLoading ? (
        <Skeleton variant='rect' width='100%' height='100%' />
      ) : (
        <>
          <TemplateItemHeader
            title={communicationType?.name || ''}
            enabled={communicationType?.status || false}
            onChange={onUpdateStatus}
            onSendTest={() => setSendTestOpen(true)}
            onExpand={() => setExpandedView(true)}
            isOptional={communicationType?.isOptional ?? true}
            disableSendTest={isInTrial}
          />
          <Box marginTop='15px'>
            <TemplateItemDescription description={communicationType?.description || ''} />
          </Box>
          <Paper
            style={{
              marginTop: '15px',
              padding: communicationType?.type === 'sms' ? '0px' : '16px 40px',
            }}
            elevation={3}
          >
            <TemplateItemBody
              key={communicationTypeId}
              greeting={greeting}
              signoff={signoff}
              logo={logo}
              content={parsedContent(content)}
              preferredName={user?.preferredName || 'Volaby User'}
              title={communicationType?.name || ''}
              contentBlocks={communicationType?.contentBlocks || []}
              onSaveContentBlock={onSaveContentBlock}
              saveContentLoading={saveCommunicationTypeContentBlock.isLoading}
              type={communicationType?.type ?? 'email'}
              hasCustomTemplate={communicationType?.hasCustomTemplate ?? false}
              availableMergeTags={communicationType?.availableMergeTags ?? []}
              isCustomizing={isCustomizing}
            />
          </Paper>
          {communicationType?.type !== 'sms' && (
            <Box display='flex' justifyContent='space-between' alignItems='center' marginTop='12px'>
              {isCustom || isCustomizing ? (
                <>
                  <Box className={classes.badge}>
                    <Edit className='icon' />
                    <Typography className='text'>Custom</Typography>
                  </Box>
                  {locked ? (
                    <EnterpriseLockNotice tooltip={templateLockTooltip} />
                  ) : (
                    <Typography
                      style={{ color: '#7047EB', cursor: 'pointer', fontSize: 12, fontWeight: 400 }}
                      onClick={onResetCustomTemplate}
                    >
                      Reset to default
                    </Typography>
                  )}
                </>
              ) : (
                <>
                  <Box />
                  {locked ? (
                    <EnterpriseLockNotice tooltip={templateLockTooltip} />
                  ) : (
                    <Typography
                      style={{ color: '#7047EB', cursor: 'pointer', fontSize: 12, fontWeight: 400 }}
                      onClick={() => setIsCustomizing(true)}
                    >
                      Override entire template
                    </Typography>
                  )}
                </>
              )}
            </Box>
          )}
          <Dialog open={sendTestOpen} onClose={onClose}>
            <DialogTitle>{`Send Test Email: ${communicationType?.name}`}</DialogTitle>
            <DialogContent>
              <AlertCard variant='info' title={communicationType?.description} />
              <Typography style={{ marginTop: '12px' }}>Email address:</Typography>
              <TextField
                style={{ marginTop: '8px' }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position='start'>
                      <Email />
                    </InputAdornment>
                  ),
                  onKeyPress: (event) => {
                    const { key } = event;
                    if (key === 'Enter') {
                      sendButtonRef.current.click();
                    }
                  },
                }}
                fullWidth
                variant='outlined'
                value={email}
                onChange={(event) => setEmail(event.target.value)}
              />
            </DialogContent>
            <DialogActions style={{ padding: '20px 24px' }}>
              <Button variant='outlined' disableElevation onClick={onClose} className={classes.button}>
                Cancel
              </Button>
              <Button
                variant='contained'
                disableElevation
                color='primary'
                ref={sendButtonRef}
                className={classes.button}
                onClick={onSend}
                disabled={email.length === 0}
              >
                Send
              </Button>
            </DialogActions>
          </Dialog>
          <Dialog
            open={expandedView}
            onClose={() => setExpandedView(false)}
            maxWidth={false}
            PaperProps={{
              style: {
                width: '90vw',
                height: '90vh',
                maxWidth: '90vw',
                maxHeight: '90vh',
                display: 'flex',
                flexDirection: 'column',
              },
            }}
          >
            <DialogTitle style={{ flexShrink: 0 }}>
              <Box display='flex' alignItems='center' justifyContent='space-between'>
                <TemplateItemHeader
                  title={communicationType?.name || ''}
                  enabled={communicationType?.status || false}
                  onChange={onUpdateStatus}
                  onSendTest={() => setSendTestOpen(true)}
                  onExpand={() => {}}
                  isOptional={communicationType?.isOptional ?? true}
                  disableSendTest={isInTrial}
                />
                <IconButton onClick={() => setExpandedView(false)} size='small' style={{ marginLeft: '8px' }}>
                  <Close />
                </IconButton>
              </Box>
            </DialogTitle>
            <DialogContent style={{ flex: 1, overflow: 'auto', padding: '16px 30px' }}>
              <TemplateItemDescription description={communicationType?.description || ''} />
              <Paper
                style={{
                  marginTop: '15px',
                  padding: communicationType?.type === 'sms' ? '0px' : '16px 40px',
                }}
                elevation={3}
              >
                <TemplateItemBody
                  key={`expanded-${communicationTypeId}`}
                  greeting={greeting}
                  signoff={signoff}
                  logo={logo}
                  content={parsedContent(content)}
                  preferredName={user?.preferredName || 'Volaby User'}
                  title={communicationType?.name || ''}
                  contentBlocks={communicationType?.contentBlocks || []}
                  onSaveContentBlock={onSaveContentBlock}
                  saveContentLoading={saveCommunicationTypeContentBlock.isLoading}
                  type={communicationType?.type ?? 'email'}
                  hasCustomTemplate={communicationType?.hasCustomTemplate ?? false}
                  availableMergeTags={communicationType?.availableMergeTags ?? []}
                  isCustomizing={isCustomizing}
                />
              </Paper>
              {communicationType?.type !== 'sms' && (
                <Box display='flex' justifyContent='space-between' alignItems='center' marginTop='12px'>
                  {isCustom || isCustomizing ? (
                    <>
                      <Box className={classes.badge}>
                        <Edit className='icon' />
                        <Typography className='text'>Custom</Typography>
                      </Box>
                      {locked ? (
                        <EnterpriseLockNotice tooltip={templateLockTooltip} />
                      ) : (
                        <Typography
                          style={{ color: '#7047EB', cursor: 'pointer', fontSize: 12, fontWeight: 400 }}
                          onClick={onResetCustomTemplate}
                        >
                          Reset to default
                        </Typography>
                      )}
                    </>
                  ) : (
                    <>
                      <Box />
                      {locked ? (
                        <EnterpriseLockNotice tooltip={templateLockTooltip} />
                      ) : (
                        <Typography
                          style={{ color: '#7047EB', cursor: 'pointer', fontSize: 12, fontWeight: 400 }}
                          onClick={() => setIsCustomizing(true)}
                        >
                          Override entire template
                        </Typography>
                      )}
                    </>
                  )}
                </Box>
              )}
            </DialogContent>
          </Dialog>
          <ConfirmationDialog
            open={resetConfirmOpen}
            title='Reset to default template?'
            body='This will remove your custom template and revert to the system default. This action cannot be undone.'
            closeActionText='Cancel'
            confirmActionText='Reset'
            handleCloseClick={() => setResetConfirmOpen(false)}
            handleConfirmClick={handleConfirmReset}
          />
        </>
      )}
    </Paper>
  );
}
